import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Configuration } from "src/app/app.constants";
import { Observable } from "rxjs";
import { AccountLocalisationDictionaryViewModel } from "../../shared/models/accountLocalisationDictionary";
import { ExternalAuthService } from "./external-auth.service";
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { JobItemViewModel } from "../../incidents/viewModels/jobs/jobItemViewModel";

@Injectable({
  providedIn: 'root'
})
export class ExternalService {
  private baseUrl: string;
  constructor(
    private http: HttpClient,
    private router: Router,
    private configuration: Configuration,
    private externalAuthService: ExternalAuthService
  ) {}

  private getAuthHeaders(includeContentType: boolean = true): HttpHeaders {
    // Get token from the current authenticated session
    const token = this.externalAuthService.getToken();

    if (!token) {
      this.externalAuthService.catchUnauthorized();
    }

    const headers: any = {
      'Authorization': `Bearer ${token}`
    };

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    return new HttpHeaders(headers);
  }

  /**
   * Get Account Language Settings (returns all data)
   * GET /v1/ExternalAccount/{externalShareId}/GetAccountLanguageSettings
   * @param externalShareId - The external share ID
   */
  getAccountLanguageSettings(externalShareId: string): Observable<AccountLocalisationDictionaryViewModel[]> {
    const url = this.configuration.buildEndpoint('ExternalAccountSettings/');
    return this.http.get<AccountLocalisationDictionaryViewModel[]>(
      `${url}${externalShareId}/GetAccountLanguageSettings`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  // ==================== ExternalJobController ====================

  /**
   * Get job details
   * GET /v1/ExternalJob/{externalShareId}/GetDetails/{jobId}
   * @param externalShareId - The external share ID
   * @param jobId - The job ID
   */
  getJobDetails(externalShareId: string, jobId: string): Observable<JobItemViewModel> {
    const url = this.configuration.buildEndpoint('ExternalJob/');
    return this.http.get<any>(
      `${url}${externalShareId}/GetDetails/${jobId}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  /**
   * Update job
   * POST /v1/ExternalJob/{externalShareId}/Update
   * @param externalShareId - The external share ID
   * @param jobData - The job data to update
   */
  updateJob(externalShareId: string, jobData: JobItemViewModel): Observable<JobItemViewModel> {
    const url = this.configuration.buildEndpoint('ExternalJob/');
    return this.http.post<any>(
      `${url}${externalShareId}/Update`,
      jobData,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  // ==================== ExternalIncidentController ====================

  /**
   * Get incident details
   * GET /v1/ExternalIncident/{externalShareId}/GetDetails/{incidentId}
   * @param externalShareId - The external share ID
   * @param incidentId - The incident ID
   */
  getIncidentDetails(externalShareId: string, incidentId: string): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalIncident/');
    return this.http.get<any>(
      `${url}${externalShareId}/GetDetails/${incidentId}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  // ==================== ExternalChecklistItemController ====================

  /**
   * Get checklist items
   * GET /v1/ExternalChecklistItem/{externalShareId}/GetListById/{globalObjectId}/{globalObjectType}
   * @param externalShareId - The external share ID
   * @param globalObjectId - The global object ID
   * @param globalObjectType - The global object type
   */
  getChecklistItems(externalShareId: string, globalObjectId: string, globalObjectType: string): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalChecklistItem/');
    return this.http.get<any>(
      `${url}${externalShareId}/GetListById/${globalObjectId}/${globalObjectType}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  /**
   * Update checklist item
   * PUT /v1/ExternalChecklistItem/{externalShareId}/Update
   * @param externalShareId - The external share ID
   * @param checklistItemData - The checklist item data to update
   */
  updateChecklistItem(externalShareId: string, checklistItemData: any): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalChecklistItem/');
    return this.http.put<any>(
      `${url}${externalShareId}/Update`,
      checklistItemData,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  // ==================== ExternalZonesController ====================

  /**
   * Get all zones
   * GET /v1/ExternalZones/{externalShareId}/Zones/All
   * @param externalShareId - The external share ID
   */
  getAllZones(externalShareId: string): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalIncidentsSettings/');
    return this.http.get<any>(
      `${url}${externalShareId}/Zones/All`,
      { headers: this.getAuthHeaders() }
    );
  }

  // ==================== ExternalAreasController ====================

  /**
   * Get all areas
   * GET /v1/ExternalAreas/{externalShareId}/Areas/All
   * @param externalShareId - The external share ID
   */
  getAllAreas(externalShareId: string): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalIncidentsSettings/');
    return this.http.get<any>(
      `${url}${externalShareId}/Areas/All`,
      { headers: this.getAuthHeaders() }
    );
  }

  // ==================== ExternalDocumentsController ====================

  /**
   * Upload part of document
   * POST /v1/ExternalDocuments/{externalShareId}/UploadPart/{part}/{originalFileName}
   * @param externalShareId - The external share ID
   * @param part - The part number
   * @param originalFileName - The original file name
   * @param fileData - The file data
   */
  uploadDocumentPart(externalShareId: string, part: number, originalFileName: string, fileData: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', fileData, fileData.name);
    const url = this.configuration.buildEndpoint('ExternalDocuments/');
    return this.http.post<any>(
      `${url}${externalShareId}/UploadPart/${part}/${originalFileName}`,
      formData,
      { headers: this.getAuthHeaders(false) } // Don't set Content-Type for FormData
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  /**
   * Complete upload
   * POST /v1/ExternalDocuments/{externalShareId}/CompleteUpload/{originalFileName}/{newFileName}/{isAttachment}/{isAccountLogo}
   * @param externalShareId - The external share ID
   * @param originalFileName - The original file name
   * @param newFileName - The new file name
   * @param isAttachment - Whether it's an attachment
   * @param isAccountLogo - Whether it's an account logo
   * @param uploadData - Optional data for completing the upload
   */
  completeDocumentUpload(
    externalShareId: string,
    originalFileName: string,
    newFileName: string,
    isAttachment: boolean,
    isAccountLogo: boolean,
    uploadData?: any
  ): Observable<string> {
    const url = this.configuration.buildEndpoint('ExternalDocuments/');
    return this.http.post<string>(
      `${url}${externalShareId}/CompleteUpload/${originalFileName}/${newFileName}/${isAttachment}/${isAccountLogo}`,
      uploadData || {},
      {
        headers: this.getAuthHeaders(),
        responseType: 'text' as 'json'
      }
    );
  }

  /**
   * Get documents by object
   * GET /v1/ExternalDocuments/{externalShareId}/ByObjectId/{objectId}/{objectType}
   * @param externalShareId - The external share ID
   * @param objectId - The object ID
   * @param objectType - The object type
   */
  getDocumentsByObject(externalShareId: string, objectId: string, objectType: string): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalDocuments/');
    return this.http.get<any>(
      `${url}${externalShareId}/ByObjectId/${objectId}/${objectType}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  /**
   * Add document
   * POST /v1/ExternalDocuments/{externalShareId}/AddDocument
   * @param externalShareId - The external share ID
   * @param documentData - The document data
   */
  addDocument(externalShareId: string, documentData: any): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalDocuments/');
    return this.http.post<any>(
      `${url}${externalShareId}/AddDocument`,
      documentData,
      { headers: this.getAuthHeaders() }
    ).pipe(
      this.externalAuthService.catchUnauthorized()
    );
  }

  /**
   * Get checklist documents
   * GET /v1/ExternalDocuments/{externalShareId}/ObjectChecklistDocuments/{objectId}/{objectType}
   * @param externalShareId - The external share ID
   * @param objectId - The object ID
   * @param objectType - The object type
   */
  getObjectChecklistDocuments(externalShareId: string, objectId: number, objectType: ObjectTypes): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalDocuments/');
    return this.http.get<any>(
      `${url}${externalShareId}/ObjectChecklistDocuments/${objectId}/${objectType}`,
      { headers: this.getAuthHeaders() }
    );
  }

  // ==================== ExternalDocumentCategoriesController ====================

  /**
   * Get document categories with documents
   * GET /v1/ExternalDocumentCategories/{externalShareId}/AllByAccountIdWithDocuments
   * @param externalShareId - The external share ID
   */
  getDocumentCategoriesWithDocuments(externalShareId: string): Observable<any> {
    const url = this.configuration.buildEndpoint('ExternalDocumentCategories/');
    return this.http.get<any>(
      `${url}${externalShareId}/AllByAccountIdWithDocuments`,
      { headers: this.getAuthHeaders() }
    );
  }
}
